import { defineMessages } from "../define";

/** Administration: users, branches, cameras (incl. camera control) and pizza types. */
export default defineMessages({
  en: {
    // users: page and list
    "users.page.title": "User management",
    "users.list.filterPlaceholder": "Filter by email/login or name…",
    "users.list.roleFilter": "Filter by role",
    "users.list.allRoles": "All roles",
    "users.list.branchFilter": "Filter by branch",
    "users.list.addUser": "Add user",
    "users.list.empty": "No users found",
    "users.list.showing.one": "Showing {shown} of {count} user",
    "users.list.showing.few": "Showing {shown} of {count} users",
    "users.list.showing.other": "Showing {shown} of {count} users",
    "users.list.total.one": "Total: {count} user",
    "users.list.total.few": "Total: {count} users",
    "users.list.total.other": "Total: {count} users",
    // users: fields (form labels and columns)
    "users.fields.login": "Email/login",
    "users.fields.password": "Password",
    "users.fields.firstName": "First name",
    "users.fields.lastName": "Last name",
    "users.fields.role": "Role",
    "users.fields.branchAccess": "Branch access",
    "users.fields.updatedAt": "Last updated",
    "users.fields.selectRole": "Select role",
    "users.fields.selectBranch": "Select branch",
    "users.fields.allBranches": "All branches",
    // users: status, role and branch changes in the table
    "users.status.active": "Active",
    "users.status.inactive": "Inactive",
    "users.status.cannotDeactivateSelf": "You cannot deactivate your own account",
    "users.status.activated": "User activated",
    "users.status.deactivated": "User deactivated",
    "users.status.updateFailed": "Failed to update the user status",
    "users.role.updated": "User role updated",
    "users.role.updateFailed": "Failed to update the user role",
    "users.branch.updated": "Branch access updated",
    "users.branch.updateFailed": "Failed to update branch access",
    // users: row menu
    "users.actions.openMenu": "Open menu",
    "users.actions.edit": "Edit user",
    "users.actions.changePassword": "Change password",
    "users.actions.activate": "Activate",
    "users.actions.deactivate": "Deactivate",
    // users: create / edit form
    "users.form.createTitle": "New user",
    "users.form.editTitle": "Edit user",
    "users.form.createDescription": "Add a new user to the system.",
    "users.form.editDescription": "Change the user's details.",
    "users.form.passwordPlaceholder": "Enter password",
    "users.form.requiredFields": "Please fill in all required fields",
    "users.form.saving": "Saving…",
    "users.form.create": "Create user",
    "users.form.saveChanges": "Save changes",
    "users.form.created": "User created",
    "users.form.createFailed": "Failed to create the user",
    "users.form.updated": "User updated",
    "users.form.updateFailed": "Failed to update the user",
    // users: password dialog
    "users.password.description": "Set a new password for {login}.",
    "users.password.newPassword": "New password",
    "users.password.newPasswordPlaceholder": "Enter a new password",
    "users.password.confirm": "Confirm password",
    "users.password.confirmPlaceholder": "Repeat the new password",
    "users.password.empty": "Please enter a password",
    "users.password.mismatch": "Passwords do not match",
    "users.password.noUser": "No user selected",
    "users.password.updated": "Password changed",
    "users.password.updateFailed": "Failed to change the password",

    // branches: list and table
    "branches.list.filterPlaceholder": "Filter branches…",
    "branches.table.selectAll": "Select all",
    "branches.table.selectRow": "Select row",
    "branches.table.openMenu": "Open menu",
    "branches.columns.name": "Branch name",
    "branches.columns.bbBranchId": "Branch ID",
    "branches.columns.acquisition": "Data acquisition",
    "branches.columns.bbTrigger": "BB order trigger",
    "branches.columns.evaluation": "Pizza evaluation",
    "branches.status.active": "Active",
    "branches.actions.switchTo": "Switch to branch",
    // branches: flag switches
    "branches.flags.acquisition": "Acquisition",
    "branches.flags.bbTrigger": "BB trigger",
    "branches.flags.evaluation": "Evaluation",
    "branches.flags.enabled": "{flag} enabled for {branch}",
    "branches.flags.disabled": "{flag} disabled for {branch}",
    "branches.flags.updateFailed": "Failed to update: {flag}",

    // cameras: page, list and table
    "cameras.page.title": "Cameras",
    "cameras.list.deviceIdFilter": "Filter by device ID…",
    "cameras.list.nameFilter": "Filter by name…",
    "cameras.table.selectAll": "Select all",
    "cameras.table.selectRow": "Select row",
    "cameras.table.openMenu": "Open menu",
    "cameras.columns.deviceId": "Device ID",
    "cameras.columns.branchUuid": "Branch UUID",
    "cameras.columns.assignment": "Assignment",
    "cameras.columns.token": "Token",
    "cameras.assignment.assigned": "Assigned",
    "cameras.assignment.unassigned": "Unassigned",
    "cameras.actions.copyDeviceId": "Copy device ID",
    "cameras.actions.copyToken": "Copy token",
    "cameras.actions.copyData": "Copy camera data",
    // cameras: assign / unassign / reboot
    "cameras.assign.button": "Assign",
    "cameras.assign.title": "Assign camera to a branch",
    "cameras.assign.description": "Assign the camera “{name}” to a branch.",
    "cameras.assign.loadingBranches": "Loading branches…",
    "cameras.assign.noBranches": "No branches available",
    "cameras.assign.selectBranch": "Select a branch",
    "cameras.assign.selectBranchFirst": "Please select a branch",
    "cameras.assign.submit": "Assign camera",
    "cameras.assign.success": "Camera {name} assigned to the branch",
    "cameras.assign.failed": "Failed to assign the camera to the branch",
    "cameras.unassign.button": "Unassign",
    "cameras.unassign.title": "Unassign camera from its branch",
    "cameras.unassign.description":
      "Are you sure you want to unassign the camera “{name}” from its current branch?",
    "cameras.unassign.submit": "Unassign camera",
    "cameras.unassign.success": "Camera {name} unassigned from its branch",
    "cameras.unassign.failed": "Failed to unassign the camera from its branch",
    "cameras.reboot.button": "Reboot",
    "cameras.reboot.title": "Reboot camera",
    "cameras.reboot.description":
      "Are you sure you want to reboot the camera “{name}”? This restarts the camera service.",
    "cameras.reboot.submit": "Reboot camera",
    "cameras.reboot.success": "Reboot of camera {name} started",
    "cameras.reboot.failed": "Failed to start the camera reboot",
    // cameras: connectivity status
    "cameras.status.online": "Online",
    "cameras.status.offline": "Offline",
    "cameras.status.unknown": "Unknown",
    "cameras.status.lastConnect": "Last connected",
    "cameras.status.lastDisconnect": "Last disconnected",
    "cameras.status.lastActivity": "Last activity",
    "cameras.status.unavailable": "Status could not be read from ThingsBoard",
    // cameras: detail
    "cameras.detail.title": "Camera detail",
    "cameras.detail.selectHint": "Select a camera in the list to see its details.",
    "cameras.detail.noneSelected": "No camera selected",
    "cameras.detail.deviceIdValue": "Device ID: {id}",
    // cameras: control (shared)
    "cameras.control.unknownError": "Unknown error",
    "cameras.control.modeAuto": "Auto",
    "cameras.control.modeManual": "Manual",
    "cameras.control.quality": "Quality",
    // cameras: exposure
    "cameras.exposure.title": "Exposure",
    "cameras.exposure.liveValues": "{mode} · gain {gain}x · {time} µs",
    "cameras.exposure.gain": "Analog gain",
    "cameras.exposure.time": "Exposure time (µs)",
    "cameras.exposure.apply": "Apply exposure",
    "cameras.exposure.applied": "Exposure applied",
    "cameras.exposure.failed": "Failed to set exposure: {error}",
    // cameras: white balance
    "cameras.whiteBalance.title": "White balance",
    "cameras.whiteBalance.liveValues": "{mode} · R {red} · B {blue}",
    "cameras.whiteBalance.redGain": "Red gain",
    "cameras.whiteBalance.blueGain": "Blue gain",
    "cameras.whiteBalance.apply": "Apply white balance",
    "cameras.whiteBalance.applied": "White balance applied",
    "cameras.whiteBalance.failed": "Failed to set white balance: {error}",
    // cameras: focus
    "cameras.focus.title": "Focus",
    "cameras.focus.modeContinuous": "Continuous",
    "cameras.focus.liveValues": "{mode} · lens {position} · {state}",
    "cameras.focus.autofocus": "Autofocus (full frame)",
    "cameras.focus.continuous": "Continuous AF",
    "cameras.focus.roiHint":
      "To focus on a specific spot, drag a rectangle (ROI) on the photo and click “{button}”.",
    "cameras.focus.lensPosition": "Manual lens position (0 = infinity)",
    "cameras.focus.applyManual": "Apply manual focus",
    "cameras.focus.manualApplied": "Manual focus applied (lens position {position})",
    "cameras.focus.continuousEnabled": "Continuous autofocus enabled",
    "cameras.focus.focused": "Focused: lens position {position} ({duration} ms)",
    "cameras.focus.notConverged": "Autofocus did not converge ({state})",
    "cameras.focus.unknownState": "unknown",
    "cameras.focus.failed": "Failed to set focus: {error}",
    "cameras.focus.autofocusFailed": "Autofocus failed: {error}",
    // cameras: live photo
    "cameras.photo.title": "Live photo",
    "cameras.photo.description": "Drag a rectangle on the photo to select the focus area (ROI).",
    "cameras.photo.autoRefresh": "Auto refresh",
    "cameras.photo.seconds": "{seconds}s",
    "cameras.photo.take": "Take photo",
    "cameras.photo.alt": "Camera service photo",
    "cameras.photo.capturing": "Capturing… (takes a few seconds)",
    "cameras.photo.empty": "No photo yet – click “{button}”",
    "cameras.photo.source": "via {source}",
    "cameras.photo.focusOnRoi": "Focus on ROI",
    "cameras.photo.focusedOnRoi": "Focused on ROI: lens position {position} ({duration} ms)",
    "cameras.photo.failed": "Failed to take a photo: {error}",
    // cameras: stream
    "cameras.stream.title": "Stream",
    "cameras.stream.endpoint": "Ingest server URL",
    "cameras.stream.invalidEndpoint": "Enter the ingest URL, e.g. http://server:8888/ingest/name",
    "cameras.stream.start": "Start",
    "cameras.stream.stop": "Stop",
    "cameras.stream.started": "Stream started",
    "cameras.stream.stopped": "Stream stopped",
    "cameras.stream.startFailed": "Failed to start the stream: {error}",
    "cameras.stream.stopFailed": "Failed to stop the stream: {error}",
    "cameras.stream.openLiveView": "Open live view",
    "cameras.stream.showPreview": "Show preview",
    "cameras.stream.hidePreview": "Hide preview",
    "cameras.stream.alt": "Live camera stream",

    // pizza types: page and list
    "pizzaTypes.page.title": "Pizza types",
    "pizzaTypes.list.loading": "Loading pizza types…",
    "pizzaTypes.list.loadFailed": "Failed to load pizza types.",
    "pizzaTypes.list.retry": "Try again",
    "pizzaTypes.list.nameFilter": "Filter by name…",
    "pizzaTypes.list.ingredientFilter": "Filter by ingredient…",
    "pizzaTypes.list.empty": "No pizza types found",
    "pizzaTypes.table.selectAll": "Select all",
    "pizzaTypes.table.selectRow": "Select row",
    "pizzaTypes.table.openMenu": "Open menu",
    "pizzaTypes.columns.id": "ID",
    "pizzaTypes.columns.ingredients": "Ingredients",
    "pizzaTypes.columns.ingredientCount": "Ingredient count",
    "pizzaTypes.actions.copyId": "Copy pizza type ID",
    "pizzaTypes.actions.copyName": "Copy name",
    "pizzaTypes.actions.copyData": "Copy pizza type data",
    // pizza types: detail
    "pizzaTypes.detail.loadFailedTitle": "Failed to load the pizza type",
    "pizzaTypes.detail.loadFailed": "Could not load the pizza type details. Please try again.",
    "pizzaTypes.detail.notFoundTitle": "Pizza type not found",
    "pizzaTypes.detail.notFound": "The requested pizza type could not be found.",
    "pizzaTypes.detail.id": "Pizza type ID",
    "pizzaTypes.detail.ingredientCount.one": "{count} ingredient",
    "pizzaTypes.detail.ingredientCount.few": "{count} ingredients",
    "pizzaTypes.detail.ingredientCount.other": "{count} ingredients",
    "pizzaTypes.detail.noIngredients": "This pizza type has no ingredients defined.",
    "pizzaTypes.detail.recentPizzas": "Recent pizzas ({count})",
    "pizzaTypes.detail.evaluated": "Evaluated",
    "pizzaTypes.detail.order": "Order: {id}",
    "pizzaTypes.detail.noRecentPizzas": "No recent pizzas of this type.",
    "pizzaTypes.annotation.raw": "Raw",
    "pizzaTypes.annotation.burnt": "Burnt",
    "pizzaTypes.annotation.ingredients": "Ingredients",
  },
  cs: {
    // users: page and list
    "users.page.title": "Správa uživatelů",
    "users.list.filterPlaceholder": "Filtrovat podle e-mailu nebo jména…",
    "users.list.roleFilter": "Filtrovat podle role",
    "users.list.allRoles": "Všechny role",
    "users.list.branchFilter": "Filtrovat podle pobočky",
    "users.list.addUser": "Přidat uživatele",
    "users.list.empty": "Nenalezen žádný uživatel",
    "users.list.showing.one": "Zobrazeno {shown} z {count} uživatele",
    "users.list.showing.few": "Zobrazeno {shown} z {count} uživatelů",
    "users.list.showing.other": "Zobrazeno {shown} z {count} uživatelů",
    "users.list.total.one": "Celkem {count} uživatel",
    "users.list.total.few": "Celkem {count} uživatelé",
    "users.list.total.other": "Celkem {count} uživatelů",
    // users: fields (form labels and columns)
    "users.fields.login": "E-mail / přihlašovací jméno",
    "users.fields.password": "Heslo",
    "users.fields.firstName": "Jméno",
    "users.fields.lastName": "Příjmení",
    "users.fields.role": "Role",
    "users.fields.branchAccess": "Přístup k pobočkám",
    "users.fields.updatedAt": "Naposledy upraveno",
    "users.fields.selectRole": "Vyberte roli",
    "users.fields.selectBranch": "Vyberte pobočku",
    "users.fields.allBranches": "Všechny pobočky",
    // users: status, role and branch changes in the table
    "users.status.active": "Aktivní",
    "users.status.inactive": "Neaktivní",
    "users.status.cannotDeactivateSelf": "Vlastní účet nemůžete deaktivovat",
    "users.status.activated": "Uživatel byl aktivován",
    "users.status.deactivated": "Uživatel byl deaktivován",
    "users.status.updateFailed": "Stav uživatele se nepodařilo změnit",
    "users.role.updated": "Role uživatele byla změněna",
    "users.role.updateFailed": "Roli uživatele se nepodařilo změnit",
    "users.branch.updated": "Přístup k pobočkám byl změněn",
    "users.branch.updateFailed": "Přístup k pobočkám se nepodařilo změnit",
    // users: row menu
    "users.actions.openMenu": "Otevřít nabídku",
    "users.actions.edit": "Upravit uživatele",
    "users.actions.changePassword": "Změnit heslo",
    "users.actions.activate": "Aktivovat",
    "users.actions.deactivate": "Deaktivovat",
    // users: create / edit form
    "users.form.createTitle": "Nový uživatel",
    "users.form.editTitle": "Upravit uživatele",
    "users.form.createDescription": "Přidejte do systému nového uživatele.",
    "users.form.editDescription": "Upravte údaje uživatele.",
    "users.form.passwordPlaceholder": "Zadejte heslo",
    "users.form.requiredFields": "Vyplňte prosím všechna povinná pole",
    "users.form.saving": "Ukládání…",
    "users.form.create": "Vytvořit uživatele",
    "users.form.saveChanges": "Uložit změny",
    "users.form.created": "Uživatel byl vytvořen",
    "users.form.createFailed": "Uživatele se nepodařilo vytvořit",
    "users.form.updated": "Uživatel byl upraven",
    "users.form.updateFailed": "Uživatele se nepodařilo upravit",
    // users: password dialog
    "users.password.description": "Nastavte nové heslo pro {login}.",
    "users.password.newPassword": "Nové heslo",
    "users.password.newPasswordPlaceholder": "Zadejte nové heslo",
    "users.password.confirm": "Potvrzení hesla",
    "users.password.confirmPlaceholder": "Zopakujte nové heslo",
    "users.password.empty": "Zadejte prosím heslo",
    "users.password.mismatch": "Hesla se neshodují",
    "users.password.noUser": "Není vybrán žádný uživatel",
    "users.password.updated": "Heslo bylo změněno",
    "users.password.updateFailed": "Heslo se nepodařilo změnit",

    // branches: list and table
    "branches.list.filterPlaceholder": "Filtrovat pobočky…",
    "branches.table.selectAll": "Vybrat vše",
    "branches.table.selectRow": "Vybrat řádek",
    "branches.table.openMenu": "Otevřít nabídku",
    "branches.columns.name": "Název pobočky",
    "branches.columns.bbBranchId": "ID pobočky",
    "branches.columns.acquisition": "Sběr dat",
    "branches.columns.bbTrigger": "Spouštění z objednávek BB",
    "branches.columns.evaluation": "Vyhodnocení pizz",
    "branches.status.active": "Aktivní",
    "branches.actions.switchTo": "Přepnout na pobočku",
    // branches: flag switches
    "branches.flags.acquisition": "Sběr dat",
    "branches.flags.bbTrigger": "Spouštění z BB",
    "branches.flags.evaluation": "Vyhodnocení",
    "branches.flags.enabled": "{flag}: zapnuto pro pobočku {branch}",
    "branches.flags.disabled": "{flag}: vypnuto pro pobočku {branch}",
    "branches.flags.updateFailed": "Nepodařilo se změnit: {flag}",

    // cameras: page, list and table
    "cameras.page.title": "Kamery",
    "cameras.list.deviceIdFilter": "Filtrovat podle ID zařízení…",
    "cameras.list.nameFilter": "Filtrovat podle názvu…",
    "cameras.table.selectAll": "Vybrat vše",
    "cameras.table.selectRow": "Vybrat řádek",
    "cameras.table.openMenu": "Otevřít nabídku",
    "cameras.columns.deviceId": "ID zařízení",
    "cameras.columns.branchUuid": "UUID pobočky",
    "cameras.columns.assignment": "Přiřazení",
    "cameras.columns.token": "Token",
    "cameras.assignment.assigned": "Přiřazená",
    "cameras.assignment.unassigned": "Nepřiřazená",
    "cameras.actions.copyDeviceId": "Kopírovat ID zařízení",
    "cameras.actions.copyToken": "Kopírovat token",
    "cameras.actions.copyData": "Kopírovat data kamery",
    // cameras: assign / unassign / reboot
    "cameras.assign.button": "Přiřadit",
    "cameras.assign.title": "Přiřadit kameru k pobočce",
    "cameras.assign.description": "Vyberte pobočku pro kameru „{name}“.",
    "cameras.assign.loadingBranches": "Načítání poboček…",
    "cameras.assign.noBranches": "Žádné pobočky nejsou k dispozici",
    "cameras.assign.selectBranch": "Vyberte pobočku",
    "cameras.assign.selectBranchFirst": "Vyberte prosím pobočku",
    "cameras.assign.submit": "Přiřadit kameru",
    "cameras.assign.success": "Kamera {name} byla přiřazena k pobočce",
    "cameras.assign.failed": "Kameru se nepodařilo přiřadit k pobočce",
    "cameras.unassign.button": "Zrušit přiřazení",
    "cameras.unassign.title": "Zrušit přiřazení kamery k pobočce",
    "cameras.unassign.description":
      "Opravdu chcete zrušit přiřazení kamery „{name}“ k její současné pobočce?",
    "cameras.unassign.submit": "Zrušit přiřazení",
    "cameras.unassign.success": "Přiřazení kamery {name} k pobočce bylo zrušeno",
    "cameras.unassign.failed": "Přiřazení kamery k pobočce se nepodařilo zrušit",
    "cameras.reboot.button": "Restartovat",
    "cameras.reboot.title": "Restartovat kameru",
    "cameras.reboot.description":
      "Opravdu chcete restartovat kameru „{name}“? Služba kamery se tím restartuje.",
    "cameras.reboot.submit": "Restartovat kameru",
    "cameras.reboot.success": "Restart kamery {name} byl zahájen",
    "cameras.reboot.failed": "Restart kamery se nepodařilo zahájit",
    // cameras: connectivity status
    "cameras.status.online": "Připojená",
    "cameras.status.offline": "Odpojená",
    "cameras.status.unknown": "Neznámý stav",
    "cameras.status.lastConnect": "Poslední připojení",
    "cameras.status.lastDisconnect": "Poslední odpojení",
    "cameras.status.lastActivity": "Poslední aktivita",
    "cameras.status.unavailable": "Stav se nepodařilo načíst z ThingsBoardu",
    // cameras: detail
    "cameras.detail.title": "Detail kamery",
    "cameras.detail.selectHint": "Vyberte kameru v seznamu a zobrazí se její podrobnosti.",
    "cameras.detail.noneSelected": "Není vybrána žádná kamera",
    "cameras.detail.deviceIdValue": "ID zařízení: {id}",
    // cameras: control (shared)
    "cameras.control.unknownError": "Neznámá chyba",
    "cameras.control.modeAuto": "Auto",
    "cameras.control.modeManual": "Ručně",
    "cameras.control.quality": "Kvalita",
    // cameras: exposure
    "cameras.exposure.title": "Expozice",
    "cameras.exposure.liveValues": "{mode} · zisk {gain}× · {time} µs",
    "cameras.exposure.gain": "Analogový zisk",
    "cameras.exposure.time": "Doba expozice (µs)",
    "cameras.exposure.apply": "Nastavit expozici",
    "cameras.exposure.applied": "Expozice nastavena",
    "cameras.exposure.failed": "Expozici se nepodařilo nastavit: {error}",
    // cameras: white balance
    "cameras.whiteBalance.title": "Vyvážení bílé",
    "cameras.whiteBalance.liveValues": "{mode} · R {red} · B {blue}",
    "cameras.whiteBalance.redGain": "Zisk červené",
    "cameras.whiteBalance.blueGain": "Zisk modré",
    "cameras.whiteBalance.apply": "Nastavit vyvážení bílé",
    "cameras.whiteBalance.applied": "Vyvážení bílé nastaveno",
    "cameras.whiteBalance.failed": "Vyvážení bílé se nepodařilo nastavit: {error}",
    // cameras: focus
    "cameras.focus.title": "Ostření",
    "cameras.focus.modeContinuous": "Průběžné",
    "cameras.focus.liveValues": "{mode} · čočka {position} · {state}",
    "cameras.focus.autofocus": "Zaostřit (celý snímek)",
    "cameras.focus.continuous": "Průběžné ostření",
    "cameras.focus.roiHint":
      "Chcete-li zaostřit na konkrétní místo, vyznačte na fotce obdélník (ROI) a klikněte na „{button}“.",
    "cameras.focus.lensPosition": "Ruční poloha čočky (0 = nekonečno)",
    "cameras.focus.applyManual": "Nastavit ruční ostření",
    "cameras.focus.manualApplied": "Ruční ostření nastaveno (poloha čočky {position})",
    "cameras.focus.continuousEnabled": "Průběžné ostření zapnuto",
    "cameras.focus.focused": "Zaostřeno: poloha čočky {position} ({duration} ms)",
    "cameras.focus.notConverged": "Automatické ostření nenašlo ostrý obraz ({state})",
    "cameras.focus.unknownState": "neznámý stav",
    "cameras.focus.failed": "Ostření se nepodařilo nastavit: {error}",
    "cameras.focus.autofocusFailed": "Automatické ostření selhalo: {error}",
    // cameras: live photo
    "cameras.photo.title": "Aktuální fotka",
    "cameras.photo.description": "Tažením vyznačte na fotce oblast pro zaostření (ROI).",
    "cameras.photo.autoRefresh": "Obnovovat",
    "cameras.photo.seconds": "{seconds} s",
    "cameras.photo.take": "Pořídit fotku",
    "cameras.photo.alt": "Servisní fotka z kamery",
    "cameras.photo.capturing": "Pořizování… (trvá několik sekund)",
    "cameras.photo.empty": "Zatím žádná fotka – klikněte na „{button}“",
    "cameras.photo.source": "zdroj: {source}",
    "cameras.photo.focusOnRoi": "Zaostřit na ROI",
    "cameras.photo.focusedOnRoi": "Zaostřeno na ROI: poloha čočky {position} ({duration} ms)",
    "cameras.photo.failed": "Fotku se nepodařilo pořídit: {error}",
    // cameras: stream
    "cameras.stream.title": "Přenos",
    "cameras.stream.endpoint": "Adresa ingest serveru",
    "cameras.stream.invalidEndpoint":
      "Zadejte adresu ingest serveru, např. http://server:8888/ingest/name",
    "cameras.stream.start": "Spustit",
    "cameras.stream.stop": "Zastavit",
    "cameras.stream.started": "Přenos spuštěn",
    "cameras.stream.stopped": "Přenos zastaven",
    "cameras.stream.startFailed": "Přenos se nepodařilo spustit: {error}",
    "cameras.stream.stopFailed": "Přenos se nepodařilo zastavit: {error}",
    "cameras.stream.openLiveView": "Otevřít živý náhled",
    "cameras.stream.showPreview": "Zobrazit náhled",
    "cameras.stream.hidePreview": "Skrýt náhled",
    "cameras.stream.alt": "Živý přenos z kamery",

    // pizza types: page and list
    "pizzaTypes.page.title": "Typy pizz",
    "pizzaTypes.list.loading": "Načítání typů pizz…",
    "pizzaTypes.list.loadFailed": "Typy pizz se nepodařilo načíst.",
    "pizzaTypes.list.retry": "Zkusit znovu",
    "pizzaTypes.list.nameFilter": "Filtrovat podle názvu…",
    "pizzaTypes.list.ingredientFilter": "Filtrovat podle suroviny…",
    "pizzaTypes.list.empty": "Nenalezen žádný typ pizzy",
    "pizzaTypes.table.selectAll": "Vybrat vše",
    "pizzaTypes.table.selectRow": "Vybrat řádek",
    "pizzaTypes.table.openMenu": "Otevřít nabídku",
    "pizzaTypes.columns.id": "ID",
    "pizzaTypes.columns.ingredients": "Suroviny",
    "pizzaTypes.columns.ingredientCount": "Počet surovin",
    "pizzaTypes.actions.copyId": "Kopírovat ID typu pizzy",
    "pizzaTypes.actions.copyName": "Kopírovat název",
    "pizzaTypes.actions.copyData": "Kopírovat data typu pizzy",
    // pizza types: detail
    "pizzaTypes.detail.loadFailedTitle": "Typ pizzy se nepodařilo načíst",
    "pizzaTypes.detail.loadFailed":
      "Podrobnosti typu pizzy se nepodařilo načíst. Zkuste to prosím znovu.",
    "pizzaTypes.detail.notFoundTitle": "Typ pizzy nebyl nalezen",
    "pizzaTypes.detail.notFound": "Požadovaný typ pizzy se nepodařilo najít.",
    "pizzaTypes.detail.id": "ID typu pizzy",
    "pizzaTypes.detail.ingredientCount.one": "{count} surovina",
    "pizzaTypes.detail.ingredientCount.few": "{count} suroviny",
    "pizzaTypes.detail.ingredientCount.other": "{count} surovin",
    "pizzaTypes.detail.noIngredients": "Tento typ pizzy nemá definované žádné suroviny.",
    "pizzaTypes.detail.recentPizzas": "Poslední pizzy ({count})",
    "pizzaTypes.detail.evaluated": "Vyhodnoceno",
    "pizzaTypes.detail.order": "Objednávka: {id}",
    "pizzaTypes.detail.noRecentPizzas": "Žádné nedávné pizzy tohoto typu.",
    "pizzaTypes.annotation.raw": "Nedopečená",
    "pizzaTypes.annotation.burnt": "Spálená",
    "pizzaTypes.annotation.ingredients": "Suroviny",
  },
});
